import { Button, Card, CardContent } from '@/ui/design-system/components';
import { DrawerStack } from './styledComponents';
import { getPathFromNode, parseTikTokTimeSeriesQuery } from 'utils/helpers';
import { useContext, useRef } from 'react';
import { BodySemi } from 'components/Text';
import CategoryDescriptionCard from 'components/CategoryDescriptionCard';
import { CreatorsTable } from 'components/Creators';
import DimensionIcon from 'components/DimensionIcon';
import DownloadTimeSeriesButton from 'components/Button/DownloadTimeSeriesButton';
import DrawerBand from 'components/DrawerBand';
import DrawerHeader from 'components/DrawerHeader';
import { buildExportFilename } from 'utils/formatCSV';
import ErrorBoundary from 'containers/ErrorBoundary';
import { FiExternalLink } from 'react-icons/fi';
import { LinkTracked } from 'components/Link';
import Stack from 'components/Stack';
import { ThemeContext } from 'styled-components';
import TikTokHashtagTimeSeries from 'components/DataVis/Charts/TikTokHashtagTimeSeries/TikTokHashtagTimeSeries';
import TikTokTimeSeriesNoDataMessage from 'components/TikTokTimeSeriesNoDataMessage';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopHashtagsDrawerSection } from 'components/TopKeywordsSection';
import { TopPostsDrawer } from 'components/Posts';
import bugsnagClient from 'containers/BugsnagClient';
import { getParentIdByAncestorHint } from 'utils/hooks/useGetHierarchy';
import { getSubcategoryLabel } from 'utils/hierarchies';
import { getTikTokData } from 'utils/api/tiktok.api';
import { useCurrentCountry, useEndDate } from 'utils/hooks';
import { useQuery } from 'react-query';
import MetricHeroCard from 'components/MetricHeroCard';
import { DATA_SOURCES } from '@/config/constants';

const CHART_HEIGHT = 287;

const queryFn = ({ queryKey }) =>
    getTikTokData({
        data: { ...queryKey[1] },
    });

function TikTokDrawerContent({
    dataPoint,
    paginateBy,
    parentHierarchies,
    showAddToWatchlist = true,
}) {
    const theme = useContext(ThemeContext);
    const country = useCurrentCountry();
    const date = useEndDate('tiktok');
    const timeSeriesRef = useRef();
    const paginateType = paginateBy === 'brands' ? 'brand' : 'category';
    const { name, category, brand, week, year, hierarchies } = dataPoint;
    const parentId = getParentIdByAncestorHint(
        hierarchies,
        parentHierarchies?.[0]?.[1],
    );
    // The tables hand down the parent's hierarchies, not the parent itself.
    const subcategory = getSubcategoryLabel(
        hierarchies,
        parentHierarchies ? { hierarchies: parentHierarchies } : null,
    );

    const tsQuery = useQuery({
        queryKey: [
            'TikTokData',
            {
                country: country ? [country.id] : null,
                brand: brand ? [brand] : null,
                category: category ? [category] : null,
                week: null,
                year: null,
            },
        ],
        queryFn,
        enabled: !!((brand || category) && country),
    });

    const tsId = tsQuery.data?.data?.[0]?.id || null;
    const timeSeries = parseTikTokTimeSeriesQuery(tsQuery, bugsnagClient);
    const timeSeriesName = `${name} (${country?.code || country?.name})`;
    const timeSeriesCsvFilename = buildExportFilename({
        entity: name,
        country,
        source: 'tiktok',
        type: paginateType,
        kind: 'time-series',
        date,
    });

    const tsData = [
        {
            id: tsId,
            name: `TikTok${country ? ` (${country.code || country.name})` : null}`,
            timeSeries,
            type: 'views',
            color: theme.tiktok_pink,
            metricLabel: 'views',
        },
    ];

    return (
        <Stack $space="1.5rem" css="padding-bottom: 2.25rem;">
            <DrawerHeader
                name={name}
                eyebrow={
                    <>
                        <DimensionIcon dimensionType={paginateType} />
                        {paginateType}
                    </>
                }
                contextLabel={subcategory}
            >
                {showAddToWatchlist && (
                    <ToggleWatch
                        id={paginateType === 'category' ? category : brand}
                        data={{
                            name,
                            type:
                                paginateType === 'category'
                                    ? 'category'
                                    : 'brand',
                        }}
                        btnChildren="Add to watchlist"
                    />
                )}
                <Button variant="default" size="sm" asChild>
                    <LinkTracked
                        to={getPathFromNode(
                            {
                                id: paginateBy === 'brands' ? brand : category,
                                type: paginateType,
                                depth: paginateBy === 'brands' ? null : 'cat5',
                            },
                            parentId,
                        )}
                        mixpanelContext="DataTableDrawer"
                        mixpanelMeta={{ itemNames: [name] }}
                    >
                        See more data
                        <FiExternalLink />
                    </LinkTracked>
                </Button>
            </DrawerHeader>
            <DrawerBand>
                <Card className="overflow-visible">
                    <MetricHeroCard
                        source={DATA_SOURCES.TIKTOK}
                        data={dataPoint}
                        healthStats={dataPoint.healthStats}
                        cluster={dataPoint?.cluster}
                    />
                </Card>
                <Card>
                    <CardContent className="p-4 flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <BodySemi as="span">Views</BodySemi>
                            <DownloadTimeSeriesButton
                                menuAlign="right"
                                csvFilename={timeSeriesCsvFilename}
                                imageTitle={timeSeriesName}
                                data={tsData}
                                wrapperRef={timeSeriesRef}
                            />
                        </div>
                        <ErrorBoundary>
                            <TikTokHashtagTimeSeries
                                chartHeight={CHART_HEIGHT}
                                data={tsData}
                                isLoading={tsQuery.isLoading}
                                showPlaceholder={
                                    tsQuery.isLoading || !timeSeries
                                }
                                placeholderMessage={
                                    !(timeSeries && timeSeries.length > 0) &&
                                    tsQuery.isLoading ? (
                                        'Loading...'
                                    ) : (
                                        <TikTokTimeSeriesNoDataMessage />
                                    )
                                }
                                wrapperRef={timeSeriesRef}
                            />
                        </ErrorBoundary>
                    </CardContent>
                </Card>
                {paginateType === 'category' ? (
                    <CategoryDescriptionCard
                        categoryId={category}
                        categoryName={name}
                        flavors={dataPoint?.flavors}
                    />
                ) : null}
            </DrawerBand>
            <DrawerStack>
                <TopHashtagsDrawerSection
                    enabled={!!((brand || category) && country && week && year)}
                    data={{
                        countries: country ? [country.id] : null,
                        brands: brand ? [brand] : null,
                        categories: category ? [category] : null,
                        week,
                        year,
                    }}
                    filename={buildExportFilename({
                        entity: name,
                        country,
                        source: 'tiktok',
                        type: 'hashtag',
                        kind: 'data',
                        date,
                    })}
                    showIcon={false}
                />
            </DrawerStack>
            <DrawerStack>
                <TopPostsDrawer
                    week={week}
                    year={year}
                    countries={country ? [country.id] : null}
                    categories={category ? [category] : null}
                    brands={brand ? [brand] : null}
                    name={name}
                    displayQty={3}
                />
            </DrawerStack>
            <DrawerStack>
                <CreatorsTable
                    year={year}
                    week={week}
                    country={country?.id || null}
                    category={category}
                    brand={brand}
                    filename={buildExportFilename({
                        entity: name || 'trend',
                        country,
                        source: 'tiktok',
                        type: 'creator',
                        kind: 'data',
                        date,
                    })}
                />
            </DrawerStack>
        </Stack>
    );
}

export default TikTokDrawerContent;
